import { useEffect, useState } from "react";
import { localCountryCode } from "../../common/local-country";
import worldUrl from "./world.json?url";

// A ring's coordinates run together, longitude then latitude: the pairs are what a path is drawn from and the
// brackets around each of them were a third of the file.
export type Ring = readonly number[];

// The outline, and then whatever is cut out of it.
export type Polygon = readonly Ring[];

// The polygons one country is drawn from, which is more than one for anywhere with an island.
export type Shape = readonly Polygon[];

export type World = Readonly<Record<string, Shape>>;

// Natural Earth's own set, drawn from who holds the ground rather than from any one country's law. It is named
// rather than left as an absence because the picker offers it and the link carries it: a fragment that merely left
// the view out would open on whatever the browser following it guesses about itself.
export const DEFAULT_VIEW = "default";

export interface Boundaries {
  // Every country as the point of view in force draws it.
  shapes: World;
  // The default view, which is what a map is framed from: a country a point of view leaves out is still somewhere.
  world: World;
  // What the view puts where it draws no country of its own — the code of the country covering that land, or the
  // empty string where it draws nothing at all and the default boundary is standing in.
  absent: Readonly<Record<string, string>>;
  // The point of view that was drawn, which the line under the map is written from.
  view: string;
}

// Undefined until the first boundaries arrive and null where they could not be read: a skeleton that never resolves
// is a page still loading as far as anybody can tell, and a map nobody is going to get should say so. A view picked
// after that leaves the map that is up until its own lands, rather than dropping the reader back to a skeleton for
// the length of a fetch — what is drawn and the sentence under it are read off the same answer, so a map showing
// the view before is a map still captioned with it.
export function useBoundaries(view: string): Boundaries | null | undefined {
  const [boundaries, setBoundaries] = useState<Boundaries | null>();

  useEffect(() => {
    let showing = true;
    read(view).then(
      (found) => showing && setBoundaries(found),
      () => showing && setBoundaries(null),
    );
    return () => {
      showing = false;
    };
  }, [view]);

  return boundaries;
}

// Both files are written the same way, the default view having countries it does not draw either: Natural Earth
// files eleven of ISO's inside the country they belong to rather than as countries of their own.
export interface View {
  absent: Record<string, string>;
  shapes: Record<string, Shape>;
}

// The two files read as one, which is what the page is handed: the view's boundaries written over the default's,
// and the default kept underneath for framing. A country the view draws as part of another one is taken off the
// map, since the land is on it already under the other country's name; one a view merely has nothing at all over
// keeps the default, a hole in the sea saying nothing that anybody drew. No published view is the second case and
// the default view's eleven never had a shape to keep, so it is there for a release that changes either. Exported
// for tests, which read the same two files off disk.
export function boundariesOf(base: View, patch: View | undefined, view: string): Boundaries {
  const world = base.shapes;
  // A view with no patch behind it is the default one however it was asked for, which is what keeps the caption
  // honest about a code that named a view Natural Earth has since dropped.
  if (!patch) return { shapes: world, world, absent: base.absent, view: DEFAULT_VIEW };

  const shapes: Record<string, Shape> = { ...world, ...patch.shapes };
  for (const [code, inside] of Object.entries(patch.absent)) {
    if (inside) delete shapes[code];
  }

  return { shapes, world, absent: { ...base.absent, ...patch.absent }, view };
}

// The views `bun run country-shapes` writes, named through a glob rather than one import each so that a view added
// upstream needs no edit here. `?url` is what keeps 2.4 MB out of this chunk: each is emitted as an asset of its
// own, hashed by the build like every other, and only the ones actually picked are ever asked for.
const VIEWS = { "./views/AR.json": __glob_0, "./views/BD.json": __glob_1, "./views/BR.json": __glob_2, "./views/CN.json": __glob_3, "./views/DE.json": __glob_4, "./views/EG.json": __glob_5, "./views/ES.json": __glob_6, "./views/FR.json": __glob_7, "./views/GB.json": __glob_8, "./views/GR.json": __glob_9, "./views/ID.json": __glob_10, "./views/IL.json": __glob_11, "./views/IN.json": __glob_12, "./views/IT.json": __glob_13, "./views/JP.json": __glob_14, "./views/KR.json": __glob_15, "./views/MA.json": __glob_16, "./views/NL.json": __glob_17, "./views/NP.json": __glob_18, "./views/PK.json": __glob_19, "./views/PL.json": __glob_20, "./views/PS.json": __glob_21, "./views/PT.json": __glob_22, "./views/RU.json": __glob_23, "./views/SA.json": __glob_24, "./views/SE.json": __glob_25, "./views/TR.json": __glob_26, "./views/TW.json": __glob_27, "./views/UA.json": __glob_28, "./views/US.json": __glob_29, "./views/VN.json": __glob_30 } as Record<
  string,
  string
>;

// The countries Natural Earth publishes a point of view for, read off the glob rather than listed again here: one
// added upstream is one more thing the picker offers and no edit at all.
export const VIEW_CODES: readonly string[] = Object.keys(VIEWS)
  .map((path) => path.slice("./views/".length, -".json".length))
  .sort();

// Total by construction, the way `pickCountry` is: a link naming a view that has since gone opens the page anyway,
// and anything the fragment does not answer is read off the reader's own country the way the country itself is.
export function pickView(code: unknown): string {
  if (code === DEFAULT_VIEW) return DEFAULT_VIEW;
  const named = typeof code === "string" ? code.toUpperCase() : undefined;
  return named && VIEW_CODES.includes(named) ? named : localView();
}

// Where the page opens for somebody who has picked nothing: their own country's view where there is one published,
// and the default everywhere else, which is what every reader outside the thirty-one used to be given with no say.
export function localView(): string {
  const code = localCountryCode();
  return VIEW_CODES.includes(code) ? code : DEFAULT_VIEW;
}

// One reading kept per view, so switching back to one already seen redraws from what is in hand rather than asking
// for it again — and the default world underneath is fetched once however many views are tried.
const READ = new Map<string, Promise<Boundaries>>();

function read(view: string): Promise<Boundaries> {
  let asked = READ.get(view);
  if (!asked) {
    READ.set(view, asked = load(view));
    // A fetch the network dropped is worth making again, so only an answer is kept.
    asked.catch(() => READ.delete(view));
  }
  return asked;
}

async function load(view: string): Promise<Boundaries> {
  const url = VIEWS[`./views/${view}.json`];
  const [base, patch] = await Promise.all([world(), url ? json<View>(url) : undefined]);
  return boundariesOf(base, patch, view);
}

let reading: Promise<View> | undefined;

// The default view is always read, since it is what every map is framed from and what a patch is a patch on.
function world(): Promise<View> {
  if (!reading) {
    reading = json<View>(worldUrl);
    reading.catch(() => {
      reading = undefined;
    });
  }
  return reading;
}

async function json<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  return await response.json() as T;
}

import __glob_0 from "./views/AR.json?url";
import __glob_1 from "./views/BD.json?url";
import __glob_2 from "./views/BR.json?url";
import __glob_3 from "./views/CN.json?url";
import __glob_4 from "./views/DE.json?url";
import __glob_5 from "./views/EG.json?url";
import __glob_6 from "./views/ES.json?url";
import __glob_7 from "./views/FR.json?url";
import __glob_8 from "./views/GB.json?url";
import __glob_9 from "./views/GR.json?url";
import __glob_10 from "./views/ID.json?url";
import __glob_11 from "./views/IL.json?url";
import __glob_12 from "./views/IN.json?url";
import __glob_13 from "./views/IT.json?url";
import __glob_14 from "./views/JP.json?url";
import __glob_15 from "./views/KR.json?url";
import __glob_16 from "./views/MA.json?url";
import __glob_17 from "./views/NL.json?url";
import __glob_18 from "./views/NP.json?url";
import __glob_19 from "./views/PK.json?url";
import __glob_20 from "./views/PL.json?url";
import __glob_21 from "./views/PS.json?url";
import __glob_22 from "./views/PT.json?url";
import __glob_23 from "./views/RU.json?url";
import __glob_24 from "./views/SA.json?url";
import __glob_25 from "./views/SE.json?url";
import __glob_26 from "./views/TR.json?url";
import __glob_27 from "./views/TW.json?url";
import __glob_28 from "./views/UA.json?url";
import __glob_29 from "./views/US.json?url";
import __glob_30 from "./views/VN.json?url";
